import type { CustomTheme } from '../../../types/custom-theme';
import type { Product } from '../../../types/product';
import { mqMin } from '../../breakpoints';

const sharedMasonryStyles: CustomTheme.CSSProperties = {
  rowGap: 's120',
  [mqMin.mobile]: {
    gridTemplateColumns: '1fr',
    rowGap: 's120',
    '.featureGridItem': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      columnGap: 's40',
    },
  },
  [mqMin.tabletLandscape]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    gridAutoFlow: 'dense',
    columnGap: 's104',
    rowGap: 's104',
    '.featureGridItem': {
      display: 'grid',
      '&:nth-of-type(5n + 1), &:nth-of-type(5n + 3)': {
        columnGap: 's32',
        gridColumn: 'auto / span 2',
      },
      '&:nth-of-type(5n + 2), &:nth-of-type(5n + 5)': {
        textAlign: 'left',
        gridTemplateColumns: '1fr',
        alignSelf: 'center',
        gridRow: 'auto / span 2',
        rowGap: 's32',
        '& > div:first-of-type': {
          order: '1',
        },
      },
      '&:nth-of-type(5n + 4), &:nth-of-type(5n + 6)': {
        columnGap: 's32',
        gridColumn: '2 / span 2',
      },
      '&:nth-of-type(5n + 3), &:nth-of-type(5n + 4)': {
        gridTemplateColumns: '15fr 16fr',
      },
      '&:nth-of-type(5n + 1), &:nth-of-type(5n + 6)': {
        gridTemplateColumns: '16fr 15fr',
      },
    },
  },
};

const featureGridContainer: CustomTheme.VariantCSSKeys<ContainerVariantsType> = {
  default: {
    rowGap: 's80',
    [mqMin.tabletSmall]: {
      gridTemplateColumns: 'repeat(3, 1fr)',
      columnGap: 's40',
    },
  },
  // Masonry repeats up to 6 items
  masonry: {
    ...sharedMasonryStyles,
    '&': {
      '.featureGridItem': {
        '&:nth-of-type(even)': {
          textAlign: 'right',
          [mqMin.mobile]: {
            '& > div:first-of-type': {
              order: '2',
            },
          },
        },
      },
      [mqMin.tabletLandscape]: {
        '& > div.featureGridItem': {
          '&:nth-of-type(even)': {
            textAlign: 'left',
            '& > div:first-of-type': {
              order: '0',
            },
          },
          '&:nth-of-type(5n + 3), &:nth-of-type(5n + 4)': {
            textAlign: 'right',
            'div:first-of-type': {
              order: '2',
            },
          },
        },
      },
    },
  },
  masonryReversed: {
    ...sharedMasonryStyles,
    '&': {
      [mqMin.mobile]: {
        '.featureGridItem': {
          '&:nth-of-type(odd)': {
            textAlign: 'right',
            '& > div:first-of-type': {
              order: '2',
            },
          },
        },
      },
      [mqMin.tabletLandscape]: {
        '> div.featureGridItem': {
          '&:first-of-type': {
            gridColumnStart: '2',
            '& > div:first-of-type': {
              order: '2',
            },
          },
          '&:nth-of-type(5n + 2)': {
            '& > div:first-of-type': {
              order: '-1',
            },
          },
          '&:nth-of-type(5n + 3), &:nth-of-type(5n + 4)': {
            textAlign: 'left',
            '& > div:first-of-type': {
              order: '-1',
            },
          },
        },
      },
    },
  },
  offset: {
    [mqMin.mobile]: {
      columnGap: 's80',
      '.featureGridItem': {
        '&:nth-of-type(3n+1), &:nth-of-type(3n+3)': {
          marginTop: 's40',
        },
      },
    },
  },
  cards: {},
  resources: {
    textAlign: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: 's40',
    '.featureGridItem': {
      flex: '0 1 300px',
      marginTop: 's24',
    },
  },
};

const container: Product.VariantKeyModes<ContainerVariantsType> = {
  products: {
    chart: featureGridContainer,
    chartblog: featureGridContainer,
    co: featureGridContainer,
    edu: featureGridContainer,
    scale: featureGridContainer,
    spark: featureGridContainer,
  },
};

export type ContainerVariantsType = 'default' | 'masonry' | 'masonryReversed' | 'offset' | 'cards' | 'resources';
export const containerVariants = Object.keys(featureGridContainer) as ContainerVariantsType[];

export default container;
